/**
 * Cookie policy page (PECR Reg. 6 — transparency about cookies). Public route.
 * Draft British-English copy with [BUSINESS TO CONFIRM] placeholders.
 * British English project.
 */
import { useNavigate } from 'react-router-dom';
import './PrivacyPolicy.css';

export function CookiePolicy() {
  const navigate = useNavigate();
  const goBack = () => {
    // Return to the page the user came from (Settings, SignIn, Home, etc.).
    // Fall back to the home route if there's no browser history (deep link).
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="itk-legal-page">
      <div className="itk-legal-page__inner">
        <button type="button" className="itk-legal-page__back" onClick={goBack}>
          ← Back
        </button>

        <h1>Cookie policy</h1>
        <p className="itk-legal-page__updated">
          Last updated: 6 August 2026 <span className="itk-legal-flag">[DRAFT — BUSINESS TO CONFIRM]</span>
        </p>

        <section>
          <h2>1. What cookies are</h2>
          <p>
            Cookies are small text files placed on your device by the websites you visit. They are widely
            used to make websites work efficiently and to provide information to site owners. This policy
            explains what we use, why, and how to control them.
          </p>
        </section>

        <section>
          <h2>2. The cookies we use</h2>
          <p>We group our cookies into three categories:</p>
          <table className="itk-legal-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>What it does</th>
                <th>Set when</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Strictly necessary</strong></td>
                <td>
                  <code>itk-consent</code> (localStorage) — remembers your cookie choice so we don&rsquo;t
                  show the banner every visit. Required for the consent mechanism itself.
                </td>
                <td>Always (when you make a cookie choice)</td>
              </tr>
              <tr>
                <td><strong>Analytics</strong></td>
                <td>
                  Google Tag Manager and Google Analytics 4 cookies — measure how the toolkit is used
                  (page views, events, conversions). Only set if you allow analytics cookies.
                </td>
                <td>Only after you choose &ldquo;Allow all&rdquo;</td>
              </tr>
              <tr>
                <td><strong>Marketing</strong></td>
                <td>
                  Google Ads cookies (ad_storage, ad_user_data, ad_personalization) — measure advertising
                  effectiveness. Only set if you allow analytics cookies.
                </td>
                <td>Only after you choose &ldquo;Allow all&rdquo;</td>
              </tr>
            </tbody>
          </table>
          <p>
            The Trustpilot widget also sets cookies if you have allowed analytics cookies. If you decline,
            we show a plain link to our Trustpilot page instead.
          </p>
        </section>

        <section>
          <h2>3. Controlling cookies</h2>
          <p>
            When you first visit the Idea Toolkit, a banner asks you to allow or decline analytics and
            marketing cookies. You can change your choice at any time using the{' '}
            <strong>&ldquo;Cookie settings&rdquo;</strong> link in the footer of any page — it&rsquo;s as
            easy to withdraw consent as it was to give it (UK GDPR Art. 7(3)).
          </p>
          <p>
            You can also control cookies through your browser settings. Most browsers allow you to refuse
            cookies or delete them. See{' '}
            <a href="https://ico.org.uk/your-data-matters/online/cookies/" target="_blank" rel="noopener">
              the ICO&rsquo;s guidance on managing cookies
            </a>{' '}
            for more information.
          </p>
        </section>

        <section>
          <h2>4. What happens if you decline</h2>
          <p>
            If you decline, the toolkit still works fully — you can sign in, complete the idea scan, use the
            Learning Hub, sign a confidentiality agreement and book a review call. We simply don&rsquo;t set
            analytics or marketing cookies, and we show a static Trustpilot link instead of the live widget.
          </p>
        </section>

        <section>
          <h2>5. Changes to this policy</h2>
          <p>
            We may update this policy from time to time. The &ldquo;last updated&rdquo; date at the top of the
            page will reflect the most recent revision.
          </p>
        </section>
      </div>
    </div>
  );
}